import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config.js";
import classes from "./Sidebar.module.css";

const Sidebar = ({ user, currentSessionId, isOpen, onClose }) => {
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // 1. Fetch History from Database
    useEffect(() => {
        if (!user) return;

        const fetchHistory = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/sessions/user/${user.user_id}`);
                if (response.ok) {
                    const data = await response.json();
                    setHistory(data);
                }
            } catch (error) {
                console.error("Failed to fetch history:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistory();
    }, [user]);

    // 2. Listen for instantaneous updates from Chat.jsx
    useEffect(() => {
        const handleNewSession = (e) => {
            const newSession = e.detail;
            setHistory(prev => {
                const filtered = prev.filter(s => s.session_id !== newSession.session_id);
                return [newSession, ...filtered];
            });
        };

        window.addEventListener('new_presentation_started', handleNewSession);
        return () => window.removeEventListener('new_presentation_started', handleNewSession);
    }, []);

    return (
        <>
            <div
                onClick={onClose}
                className={`${classes.sidebarOverlay} ${isOpen ? classes.overlayOpen : ""}`}
            ></div>

            <aside className={`${classes.sidebar} ${isOpen ? classes.sidebarOpen : ""}`}>
                <div className={classes.newBtnContainer}>
                    <button
                        onClick={() => {
                            navigate("/chat");
                            onClose();
                        }}
                        className={classes.newBtn}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        New Presentation
                    </button>
                </div>

                <div className={classes.sectionTitle}>
                    Your Workspace
                </div>

                <div className={classes.historyList}>
                    {isLoading ? (
                        <div className={classes.loadingState}>
                            <div className={classes.shimmer}></div>
                            Loading projects...
                        </div>
                    ) : history.length === 0 ? (
                        <div className={classes.emptyState}>
                            <div className={classes.emptyIcon}>✨</div>
                            No presentations yet.<br/>Start chatting to build one!
                        </div>
                    ) : (
                        history.map((session) => {
                            const isActive = session.session_id === currentSessionId;

                            return (
                                <div
                                    key={session.session_id}
                                    onClick={() => {
                                        navigate(`/chat/${session.session_id}`);
                                        onClose();
                                    }}
                                    className={`${classes.historyItem} ${isActive ? classes.historyItemActive : ""}`}
                                >
                                    <div className={classes.itemHeader}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${classes.itemIcon} ${isActive ? classes.iconActive : ""}`}>
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                            <line x1="9" y1="21" x2="9" y2="9"></line>
                                        </svg>
                                        <div className={`${classes.itemTitle} ${isActive ? classes.itemTitleActive : ""}`}>
                                            {session.title || "Untitled Presentation"}
                                        </div>
                                    </div>
                                    <div className={classes.itemMeta}>
                                        <span className={classes.dateText}>
                                            {new Date(session.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                        <span className={`${classes.statusBadge} ${session.pipeline_status === "DONE" ? classes.badgeReady : classes.badgeDraft}`}>
                                            {session.pipeline_status === "DONE" ? "Ready" : "Draft"}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </aside>
        </>
    );
};

export default Sidebar;